import Alert from "@mui/material/Alert";
import { Layers as LayersIcon } from "lucide-react";
import * as React from "react";

import { useAuth, useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import {
  ResourceView,
  filterToCondition,
  resourceIdFilter,
  useResourceView,
  ViewPicker,
  SaveViewControl,
  useLoadedView,
  useRecordTableState,
  useRestoredTableState,
  useViewFromLink,
  type FiltersHandle,
  type FilterDefinition,
  type SelectionOption,
} from "@/shared/components/ResourceView";
import {
  CustomFieldRemovedColumnsAlert,
  useCustomFieldGridIntegration,
} from "@/shared/components/ResourceView/customFields";
import {
  COLLECTION_RESOURCE_TYPES,
  CollectionRecord,
} from "@/shared/domain/collections/CollectionRecord";
import { collectionsTableColumns } from "@/shared/domain/collections/CollectionsTableColumns";
import { collectionQueryKeys } from "@/shared/domain/collections/hooks/collectionQueryKeys";
import { TargetEntityType } from "@/shared/domain/customFields";
import {
  composeQueryBody,
  QueryContentMode,
  QueryTarget,
} from "@/shared/domain/query";
import {
  useQueryCount,
  usePaginatedQueryResults,
  useStructuredQuery,
} from "@/shared/domain/query/hooks";
import { useCollectionsTablePreferences } from "@/shared/domain/users/hooks";
import {
  ColumnVisibility,
  DataTable,
  FLEX_FILL_COL_DEF,
  buildColumnsForPreferences,
  colId,
  type ColDef,
  type ColumnPickerOption,
  deriveLoadingState,
} from "@/shared/foundation/primitives/Table";

import styles from "./CollectionsList.module.css";
import { CreateCollectionButton } from "./CreateCollectionButton";

const STATIC_COLLECTION_PICKER_OPTIONS: ColumnPickerOption[] =
  collectionsTableColumns.map((c) => ({
    id: colId(c),
    label: c.headerName ?? colId(c),
  }));

interface CollectionsListProps {
  actions?: React.ReactNode;
  selectionOptions?: SelectionOption<CollectionRecord>[];
}

export function CollectionsList({
  actions,
  selectionOptions,
}: CollectionsListProps) {
  const { currentOrganization } = useAuth();
  const resolvedOrgId = useCurrentOrgId(currentOrganization?.org_id);
  const { collections } = useDomainServices();
  const { preferences, savePreferences } = useCollectionsTablePreferences();

  const [initialColumnIds] = React.useState(() => preferences.columns);

  const customFields = useCustomFieldGridIntegration<CollectionRecord>({
    entityType: TargetEntityType.Collection,
    preferenceOrder: initialColumnIds,
    savePreferences,
  });

  const columns = React.useMemo<ColDef<CollectionRecord>[]>(
    () =>
      buildColumnsForPreferences<CollectionRecord>({
        staticColumns: collectionsTableColumns,
        dynamicColumns: customFields.customFieldColumns,
        preferenceOrder: customFields.effectivePreferenceOrder,
      }),
    [customFields.customFieldColumns, customFields.effectivePreferenceOrder],
  );

  const availableColumns = React.useMemo<ColumnPickerOption[]>(
    () => [
      ...STATIC_COLLECTION_PICKER_OPTIONS,
      ...customFields.columnPickerOptions,
    ],
    [customFields.columnPickerOptions],
  );

  // A `?view=` link is resolved in the route loader, so the View it names
  // is available here — before the table's first query is composed. Seeding
  // it is what keeps a shared link from rendering an unfiltered page first.
  const linked = useViewFromLink<CollectionRecord>({
    getRowId: (row) => row.collection_id,
    initialSort: { columnId: "created", direction: "desc" },
    preferences,
    onPreferencesChange: savePreferences,
  });
  const restored = useRestoredTableState({
    target: QueryTarget.Collections,
    link: linked,
  });
  const view = useResourceView<CollectionRecord>(restored.options);

  // Which saved View is applied, and the routes in and out of one.
  const viewSession = useLoadedView(view, restored.view);
  useRecordTableState({
    target: QueryTarget.Collections,
    view,
    loadedView: viewSession.loadedView,
  });
  // Lets the View picker's empty state open the filter menu on a field.
  const filtersRef = React.useRef<FiltersHandle>(null);

  const filterDefinitions: FilterDefinition[] = React.useMemo(
    () => [
      {
        type: "string",
        field: "collection_id",
        name: "Collection Id",
        ...resourceIdFilter,
      },
      { type: "string", field: "name", name: "Name" },
      { type: "string", field: "description", name: "Description" },
      {
        type: "enum",
        field: "resource_type",
        name: "Entity Type",
        options: [...COLLECTION_RESOURCE_TYPES],
      },
      { type: "date", field: "created", name: "Created" },
      { type: "string", field: "created_by", name: "Created By" },
      {
        type: "set",
        field: "tags",
        name: "Tags",
        options: function getCollectionTags() {
          return collections.getTagsForOrg({
            resourceOwnerId: resolvedOrgId ?? "",
          });
        },
      },
      ...customFields.filterDefinitions,
    ],
    [collections, customFields.filterDefinitions, resolvedOrgId],
  );

  const filterCondition =
    view.filters.type === "structured" && view.filters.filters.length > 0
      ? filterToCondition(view.filters.filters, {
          matchMode: view.filters.matchMode,
        })
      : undefined;

  const query = composeQueryBody({
    scopeCondition: undefined,
    filterCondition,
    sort: view.table.sort,
  });

  const submitQuery = useStructuredQuery({
    resolvedOrgId,
    query,
    target: QueryTarget.Collections,
    contentMode: QueryContentMode.RecordOnly,
    queryKey: collectionQueryKeys.list(resolvedOrgId, query),
  });
  const queryId = submitQuery.data?.query_id;

  const pageQuery = usePaginatedQueryResults<CollectionRecord>(
    queryId,
    view.table.currentPage,
    view.table.pageSize,
  );
  const countQuery = useQueryCount(queryId);

  const rows = pageQuery.data ?? [];

  const loading = deriveLoadingState({
    isLoading: submitQuery.isLoading || pageQuery.isLoading,
    isFetching: submitQuery.isFetching || pageQuery.isFetching,
  });
  const fetchError = submitQuery.error ?? pageQuery.error;

  const hasSelectionOptions = Boolean(selectionOptions?.length);

  return (
    <>
      {fetchError && (
        <Alert severity="error" className={styles["error-banner"]}>
          {fetchError.message}
        </Alert>
      )}
      <CustomFieldRemovedColumnsAlert {...customFields.removedColumnsAlert} />
      <ResourceView.Root>
        <ResourceView.Actions>
          <ViewPicker
            target={QueryTarget.Collections}
            loadedView={viewSession.loadedView}
            onSelectView={viewSession.selectView}
            seededViewId={viewSession.seededViewId}
            arrivalClaimed={restored.arrivalClaimed}
            onClearView={viewSession.clearView}
            linkFor={viewSession.linkFor}
            filterDefinitions={filterDefinitions}
            onStartFilter={(field) => filtersRef.current?.openWithField(field)}
          />
          <ResourceView.Filters
            ref={filtersRef}
            filterDefinitions={filterDefinitions}
            onFilterSelected={view.commit}
          />
          <ColumnVisibility
            options={availableColumns}
            visibleColumnIds={view.table.visibleColumnIds}
            setColumnVisibility={view.table.setColumnVisibility}
          />
          <ResourceView.Selection<CollectionRecord>
            selection={view.table.selection}
            clearSelection={view.table.clearSelection}
            options={selectionOptions ?? []}
          />
          {actions && <div className={styles["actions-right"]}>{actions}</div>}
        </ResourceView.Actions>
        <ResourceView.ActiveFilters
          trailingActions={
            <SaveViewControl
              view={view}
              target={QueryTarget.Collections}
              session={viewSession}
            />
          }
          filters={view.filters}
          filterDefinitions={filterDefinitions}
          onRemove={view.removeFilter}
          onUpdate={view.updateFilter}
          onClear={view.clearFilters}
          onMatchModeChange={view.setMatchMode}
          onFilterSelected={view.commit}
        />
        <DataTable<CollectionRecord>
          defaultColDef={FLEX_FILL_COL_DEF}
          table={view.table}
          hasNextPage={pageQuery.hasNextPage}
          loading={loading}
          columns={columns}
          data={rows}
          noRowsContent={
            <div className={styles["empty-state"]}>
              <LayersIcon
                className={styles["empty-state-icon"]}
                aria-hidden="true"
              />
              <div className={styles["empty-state-text"]}>
                <p className={styles["empty-state-heading"]}>
                  No collections yet.
                </p>
                <p className={styles["empty-state-sub-text"]}>
                  Create one to group related resources together.
                </p>
              </div>
              <CreateCollectionButton
                label="Add Collection"
                variant="secondary"
                menuOrigin="center"
              />
            </div>
          }
          totalRowCount={countQuery.data}
          selectionEnabled={hasSelectionOptions}
        />
      </ResourceView.Root>
    </>
  );
}
